import pipelineAutomationsAPI from 'dashboard/api/pipelineAutomations';
import { normalizeAutomationFlow } from 'dashboard/routes/dashboard/pipeline/automations/utils/triggerFilterContract';

const MAX_HISTORY = 50;

const defaultTriggerNode = () => ({
  id: 'trigger_1',
  type: 'trigger',
  position: { x: 300, y: 200 },
  data: {
    label: 'Trigger',
    type: 'item_created',
    event_type: 'pipeline_item_created',
  },
});

const cloneFlow = flow => JSON.parse(JSON.stringify(flow));

const state = {
  flow: { nodes: [], connections: [] },
  automations: [],
  currentAutomation: null,
  dragConnection: {
    active: false,
    nodeId: null,
    handleType: 'source',
    position: { x: 0, y: 0 },
  },
  selectedNodeId: null,
  history: [],
  historyIndex: -1,
  uiFlags: { isFetching: false, isSaving: false },
};

const getters = {
  getAutomations: s => s.automations,
  getCurrentAutomation: s => s.currentAutomation,
  getFlowNodes: s => s.flow.nodes,
  getFlowConnections: s => s.flow.connections,
  getSelectedNode: s =>
    s.flow.nodes.find(n => n.id === s.selectedNodeId) || null,
  getAutomationById: s => id =>
    s.automations.find(a => a.id === Number(id) || a.id === id) || null,
  canUndo: s => s.historyIndex > 0,
  canRedo: s => s.historyIndex < s.history.length - 1,
};

const mutations = {
  SET_UI_FLAG(s, flags) {
    s.uiFlags = { ...s.uiFlags, ...flags };
  },
  SET_AUTOMATIONS(s, automations) {
    s.automations = automations;
  },
  SET_CURRENT_AUTOMATION(s, automation) {
    s.currentAutomation = automation;
  },
  INIT_FLOW(s, flowData) {
    if (flowData && flowData.nodes && flowData.nodes.length > 0) {
      s.flow = {
        nodes: flowData.nodes,
        connections: flowData.connections || flowData.edges || [],
      };
    } else {
      s.flow = { nodes: [defaultTriggerNode()], connections: [] };
    }
    // Reset history
    s.history = [cloneFlow(s.flow)];
    s.historyIndex = 0;
    s.selectedNodeId = null;
  },
  setSelectedNode(s, nodeId) {
    s.selectedNodeId = nodeId;
  },
  addNode(s, node) {
    // Trigger uniqueness — a flow has exactly one trigger. Dragging a 2nd
    // trigger from the toolbar is silently ignored (the toolbar UI also
    // hides triggers when one exists, but the mutation is the last line
    // of defense — e.g. import flow with two triggers, drag race).
    if (
      node.type === 'trigger' &&
      s.flow.nodes.some(n => n.type === 'trigger')
    ) {
      return;
    }
    s.flow.nodes.push(node);
  },
  updateNode(s, { nodeId, updates, addToHistory = true }) {
    const index = s.flow.nodes.findIndex(n => n.id === nodeId);
    if (index === -1) return;
    const node = s.flow.nodes[index];
    // _rev bumps on every data mutation so FlowCanvas.nodesSignature can
    // detect data changes without hashing the whole payload. Without this
    // the perf-optimized signature ignores data and downstream node
    // components (SwitchNode handles, etc) miss reactive updates.
    const dataChanged = updates.data !== undefined;
    /* eslint-disable no-underscore-dangle */
    s.flow.nodes[index] = {
      ...node,
      ...updates,
      position: updates.position ? updates.position : node.position,
      data: updates.data ? { ...node.data, ...updates.data } : node.data,
      _rev: dataChanged ? (node._rev || 0) + 1 : node._rev || 0,
    };
    /* eslint-enable no-underscore-dangle */
    if (addToHistory) {
      mutations.recordCurrentState(s);
    }
  },
  removeNode(s, nodeId) {
    s.flow.nodes = s.flow.nodes.filter(n => n.id !== nodeId);
    s.flow.connections = s.flow.connections.filter(
      c => c.source !== nodeId && c.target !== nodeId
    );
    mutations.recordCurrentState(s);
  },
  addConnection(s, connection) {
    // Dedupe — fast clicks on a handle could otherwise push N identical edges
    // (same source/target). The first one wins; later duplicates are ignored.
    const exists = s.flow.connections.some(
      c =>
        c.source === connection.source &&
        c.target === connection.target &&
        (c.sourceHandle ?? null) === (connection.sourceHandle ?? null) &&
        (c.targetHandle ?? null) === (connection.targetHandle ?? null)
    );
    if (exists) return;
    s.flow.connections.push(connection);
    mutations.recordCurrentState(s);
  },
  removeConnection(s, connectionId) {
    s.flow.connections = s.flow.connections.filter(c => c.id !== connectionId);
    mutations.recordCurrentState(s);
  },
  updateConnection(s, { connectionId, updates, addToHistory = true }) {
    const idx = s.flow.connections.findIndex(c => c.id === connectionId);
    if (idx === -1) return;
    const prev = s.flow.connections[idx];
    // _rev tracks data mutations on an edge so the FlowCanvas watcher
    // knows to imperatively call updateEdge() on Vue Flow's internal
    // store. Same problem as nodes: reassigning edges.value via v-model
    // diffs by id and keeps the original edge object for existing ids,
    // so changed label / animated never appears on the canvas.
    /* eslint-disable no-underscore-dangle */
    s.flow.connections[idx] = {
      ...prev,
      ...updates,
      _rev: (prev._rev || 0) + 1,
    };
    /* eslint-enable no-underscore-dangle */
    if (addToHistory) mutations.recordCurrentState(s);
  },
  startConnectionDrag(s, { nodeId, handleType, position }) {
    s.dragConnection = { active: true, nodeId, handleType, position };
  },
  updateDragPosition(s, position) {
    s.dragConnection.position = position;
  },
  endConnectionDrag(s) {
    s.dragConnection = {
      active: false,
      nodeId: null,
      handleType: 'source',
      position: { x: 0, y: 0 },
    };
  },
  recordCurrentState(s) {
    // Truncate any redo history
    s.history = s.history.slice(0, s.historyIndex + 1);
    s.history.push(cloneFlow(s.flow));
    if (s.history.length > MAX_HISTORY) {
      s.history.shift();
    }
    s.historyIndex = s.history.length - 1;
  },
  undo(s) {
    if (s.historyIndex <= 0) return;
    s.historyIndex -= 1;
    s.flow = cloneFlow(s.history[s.historyIndex]);
  },
  redo(s) {
    if (s.historyIndex >= s.history.length - 1) return;
    s.historyIndex += 1;
    s.flow = cloneFlow(s.history[s.historyIndex]);
  },
};

const actions = {
  async fetchAutomations({ commit }) {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const { data } = await pipelineAutomationsAPI.get();
      commit('SET_AUTOMATIONS', Array.isArray(data) ? data : []);
    } catch {
      commit('SET_AUTOMATIONS', []);
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  async fetchAutomation({ commit }, id) {
    const { data } = await pipelineAutomationsAPI.show(id);
    commit('SET_CURRENT_AUTOMATION', data);
    return data;
  },

  async saveAutomation(
    { commit, state: s },
    { id, name, description, active, flow, trigger_type }
  ) {
    commit('SET_UI_FLAG', { isSaving: true });
    try {
      const payload = {
        name,
        description,
        active,
        flow: normalizeAutomationFlow(flow),
        trigger_type: trigger_type || 'event',
      };
      let response;
      if (id) {
        response = await pipelineAutomationsAPI.update(id, payload);
      } else {
        response = await pipelineAutomationsAPI.create(payload);
      }
      const automation = response.data;
      // Refresh the automations list after save
      const current = s.automations;
      const idx = current.findIndex(a => a.id === automation.id);
      if (idx !== -1) {
        const updated = [...current];
        updated[idx] = automation;
        commit('SET_AUTOMATIONS', updated);
      } else {
        commit('SET_AUTOMATIONS', [...current, automation]);
      }
      return automation;
    } finally {
      commit('SET_UI_FLAG', { isSaving: false });
    }
  },

  async deleteAutomation({ commit, state: s }, id) {
    await pipelineAutomationsAPI.destroy(id);
    commit(
      'SET_AUTOMATIONS',
      s.automations.filter(a => a.id !== id)
    );
  },

  initFlow({ commit }, flowData = null) {
    commit('INIT_FLOW', flowData);
  },

  createNode({ commit }, { type, position, data }) {
    const id = `${type}_${Date.now()}`;
    const node = { id, type, position, data };
    commit('addNode', node);
    commit('recordCurrentState');
    return id;
  },

  // eslint-disable-next-line no-shadow
  connectNodes(
    { state: storeState, commit },
    { sourceId, targetId, sourceHandle = null, targetHandle = null, label = '' }
  ) {
    // Self-loops on a single node make no sense in our execution model
    // (each node runs once; the engine would treat it as a cycle).
    if (sourceId === targetId) return null;
    // Validate node types before letting the connection into the store.
    // Without this, the UI rejects via isValidConnection but the
    // store accumulates orphan/invalid edges that the save endpoint
    // would happily persist (e.g. trigger as target, annotation as
    // either endpoint, missing endpoint id).
    const sourceNode = storeState.flow.nodes.find(n => n.id === sourceId);
    const targetNode = storeState.flow.nodes.find(n => n.id === targetId);
    if (!sourceNode || !targetNode) return null;
    // Triggers are the entry point — they cannot have inputs.
    if (targetNode.type === 'trigger') return null;
    // Annotations are visual-only — neither source nor target of edges.
    if (sourceNode.type === 'annotation' || targetNode.type === 'annotation') {
      return null;
    }
    // The id includes the source/target handles so a switch/condition node
    // can legitimately have multiple outgoing edges (one per branch) without
    // colliding on id. Without the handle in the id, edge IDs become non-unique
    // when two clicks happen in the same millisecond from different handles.
    const handleSig = `${sourceHandle || 'r'}_${targetHandle || 'l'}`;
    const connection = {
      id: `conn_${sourceId}_${targetId}_${handleSig}_${Date.now()}`,
      source: sourceId,
      target: targetId,
      sourceHandle,
      targetHandle,
      label,
      animated: false,
    };
    commit('addConnection', connection);
    return connection;
  },

  updateNodeData({ commit }, { nodeId, data }) {
    commit('updateNode', { nodeId, updates: { data }, addToHistory: true });
  },

  async fetchExecutions(_, { automationId, params = {} }) {
    const { data } = await pipelineAutomationsAPI.executions(
      automationId,
      params
    );
    return data;
  },

  duplicateNode({ commit, state: s }, nodeId) {
    const node = s.flow.nodes.find(n => n.id === nodeId);
    if (!node) return undefined;
    const newNode = {
      ...JSON.parse(JSON.stringify(node)),
      id: `${node.type}_${Date.now()}`,
      position: { x: node.position.x + 30, y: node.position.y + 30 },
    };
    commit('addNode', newNode);
    commit('recordCurrentState');
    return newNode.id;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
